import { isSameDay } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import create from "zustand";
import { WebinarProps } from "../components/WebinarSlot";
import {
  fetchWebinars,
  getWebinarCredits,
  getWebinarPrice,
  getWebinarStartDateAndTime,
  hasScheduledResults,
  ScheduleResult,
  Webinar,
} from "../util/webinars";
import useStore from "./useStore";

type WebinarStoreType = {
  webinars: any;
  getAllWebinars: () => void;
  topics: Set<string>;
  addTopicToFilter: (topic: string) => void;
  removeTopicFromFilter: (topic: string) => void;
  credits: Set<number>;
  addCreditToFilter: (credit: number) => void;
  removeCreditFromFilter: (credit: number) => void;
};

export const topicList = [
  "Accounting &amp; Auditing",
  "Technology",
  "Management",
  "Professional Development",
  "Specialized Knowledge &amp; Applications",
  "Taxation",
  "Ethics",
  "Yellowbook",
];

export const creditsList = [1, 2, 3, 4, 5, 6, 7, 8];

export const useWebinarStore = create<WebinarStoreType>((set) => ({
  webinars: [],
  topics: new Set([]),

  addTopicToFilter: (topic: string) =>
    set((state) => {
      const newTopics = new Set(state.topics);
      newTopics.add(topic);
      return { ...state, topics: newTopics };
    }),
  removeTopicFromFilter: (topic: string) =>
    set((state) => {
      const newTopics = new Set(state.topics);
      newTopics.delete(topic);
      return { ...state, topics: newTopics };
    }),

  addCreditToFilter: (credit: number) =>
    set((state) => {
      const newCredits = new Set(state.credits);
      newCredits.add(credit);
      return { ...state, credits: newCredits };
    }),
  removeCreditFromFilter: (credit: number) =>
    set((state) => {
      const newCredits = new Set(state.credits);
      newCredits.delete(credit);
      return { ...state, credits: newCredits };
    }),

  credits: new Set([]),

  getAllWebinars: async () => {
    const webinars = (await fetchWebinars())
      .filter(hasScheduledResults)

      .map((webinar: Webinar) => ({
        ...webinar,
        ScheduleResults: webinar.ScheduleResults.map(
          (result: ScheduleResult) => ({
            ...result,
            Price: getWebinarPrice(webinar, result.Price),
            Credits: getWebinarCredits(webinar, result.Credits),
          })
        ),
      }));

    set((state: any) => ({
      ...state,
      webinars,
    }));
  },
}));

export const useAllWebinars = () => {
  const webinars = useWebinarStore((state) => state.webinars);
  const getAllWebinars = useWebinarStore((state) => state.getAllWebinars);

  useEffect(() => {
    if (!webinars || webinars.length === 0) {
      getAllWebinars();
    }
  }, [webinars, getAllWebinars]);

  return { webinars };
};

export const useTodaysWebinars = (today: Date) => {
  const allWebinars = useWebinarStore<Webinar[]>((state) => state.webinars);
  const topics = useWebinarStore((state) => state.topics);
  const credits = useWebinarStore((state) => state.credits);
  const month = useStore((state) => state.selectedMonth.value);
  const year = useStore((state) => state.selectedYear.value);
  const [todaysWebinars, setTodaysWebianrs] = useState<WebinarProps[]>([]);

  const getTodaysWebinars = useCallback(() => {
    let tmpWebinars: any = [];

    for (const webinar of allWebinars) {
      const matchesForToday = webinar.ScheduleResults.filter(
        (result: ScheduleResult) =>
          isSameDay(new Date(result.StartDateString), today)
      ).map((result: ScheduleResult) => {
        const [ProductDate, ProductTime] = getWebinarStartDateAndTime(
          result.StartDateString,
          result.EndDateString,
          result.TimeZone
        );
        return {
          ID: webinar.ID,
          Title: webinar.CourseTitle,
          DeliveryFormatName: webinar.DeliveryFormatName,
          FieldOfStudyName: webinar.FieldOfStudyName,
          ProductDate,
          ProductTime,
          IsNew: webinar.IsNew,
          Price: result.Price,
          ProductCredits: result.Credits,
          LiveEventProductId: result.LiveEventProductId,
          CourseID: webinar.ID,
          ProductID: result.LiveEventProductId,
          ProductCodes: webinar.ScheduleResults.length,
          StartDateString: result.StartDateString,
          Acronym: webinar.Acronym,
          UnitPrice: parseFloat(result.Price.slice(1)),
          IsMobile: webinar.isMobileCourse,
        };
      });

      if (matchesForToday.length > 0) {
        tmpWebinars.push(...matchesForToday);
      }
    }

    setTodaysWebianrs(tmpWebinars);
  }, [month, year, allWebinars, topics]);

  useEffect(() => {
    getTodaysWebinars();
  }, [allWebinars, month, year]);

  return todaysWebinars;
};
